import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import PropertyCard from '@/components/PropertyCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Heart, Building2, PlusCircle, Search, Pencil, Eye, Clock, CheckCircle, XCircle, Video, Calendar, User, MapPin } from 'lucide-react';
import axios from 'axios';
import ProfileCompletion from '@/components/ProfileCompletion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { t, language } = useLanguage();
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [myProperties, setMyProperties] = useState([]);
  const [myTours, setMyTours] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;

  const fetchData = () => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/users/favorites`, { headers }).then(r => setFavorites(r.data.properties)),
      axios.get(`${API}/users/properties`, { headers }).then(r => setMyProperties(r.data.properties)),
      axios.get(`${API}/tours/my`, { headers }).then(r => setMyTours(r.data.bookings || [])).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleFavToggle = async () => {
    await refreshUser();
    const res = await axios.get(`${API}/users/favorites`, { headers });
    setFavorites(res.data.properties);
  };

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle className="w-3.5 h-3.5 text-green-600" />;
    if (status === 'pending') return <Clock className="w-3.5 h-3.5 text-yellow-600" />;
    return <XCircle className="w-3.5 h-3.5 text-red-600" />;
  };

  const statusColor = (status) => {
    if (status === 'approved') return 'bg-green-100 text-green-700';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">
          {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{t('dashboard.title')}</p>
      </div>

      {user?.role === 'user' && <ProfileCompletion />}

      <Tabs defaultValue="favorites" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl" data-testid="dashboard-tabs">
          <TabsTrigger value="favorites" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-favorites">
            <Heart className="w-4 h-4" />{t('dashboard.favorites')}
            {favorites.length > 0 && <Badge className="ml-1 bg-primary text-white text-xs h-5 px-1.5">{favorites.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="properties" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-properties">
            <Building2 className="w-4 h-4" />{t('dashboard.myProperties')}
            {myProperties.length > 0 && <Badge className="ml-1 bg-primary text-white text-xs h-5 px-1.5">{myProperties.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="tours" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-tours">
            <Video className="w-4 h-4" />{tt('My Tours', 'Mis Tours')}
            {myTours.length > 0 && <Badge className="ml-1 bg-primary text-white text-xs h-5 px-1.5">{myTours.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="favorites">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3].map(i => <Skeleton key={i} className="h-72 rounded-xl" />)}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-border" data-testid="no-favorites">
              <Heart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">{t('dashboard.noFavorites')}</p>
              <Link to="/search"><Button variant="outline" className="gap-1.5"><Search className="w-4 h-4" />{t('nav.search')}</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {favorites.map((prop, i) => <PropertyCard key={prop.id} property={prop} index={i} onFavoriteToggle={handleFavToggle} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="properties">
          <div className="flex justify-end mb-4">
            <Link to="/submit">
              <Button className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="submit-new-btn">
                <PlusCircle className="w-4 h-4" />{t('nav.submit')}
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3].map(i => <Skeleton key={i} className="h-72 rounded-xl" />)}
            </div>
          ) : myProperties.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-border" data-testid="no-properties">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">{t('dashboard.noProperties')}</p>
              <Link to="/submit"><Button className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]"><PlusCircle className="w-4 h-4" />{t('nav.submit')}</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {myProperties.map(prop => (
                <div key={prop.id} className="bg-white rounded-xl border border-border p-4 flex items-center gap-4 hover:shadow-sm transition-shadow"
                  data-testid={`my-property-${prop.id}`}>
                  {/* Thumbnail */}
                  <div className="w-24 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 hidden sm:block">
                    {prop.main_photo ? (
                      <img src={prop.main_photo.startsWith('http') ? prop.main_photo : `${API}/files/${prop.main_photo}`}
                        alt={prop.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-6 h-6" /></div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm truncate">{prop.title}</h3>
                      <Badge className={`text-xs flex-shrink-0 ${statusColor(prop.status)}`}>
                        <span className="flex items-center gap-1">{statusIcon(prop.status)} {t(`property.${prop.status}`)}</span>
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{prop.city}{prop.neighborhood ? `, ${prop.neighborhood}` : ''}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      <span className="font-semibold text-primary">${prop.price.toLocaleString()}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{prop.views_count} {t('property.views')}</span>
                      <Badge variant="outline" className="text-xs capitalize">{t(`types.${prop.property_type}`)}</Badge>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" className="gap-1 h-8" onClick={() => navigate(`/property/${prop.id}`)} data-testid={`view-property-${prop.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                      <span className="hidden md:inline">{t('common.viewAll').split(' ')[0]}</span>
                    </Button>
                    <Button size="sm" className="gap-1 h-8 bg-primary hover:bg-primary/90" onClick={() => navigate(`/property/${prop.id}/edit`)} data-testid={`edit-property-${prop.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                      <span className="hidden md:inline">{t('common.edit')}</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* My Tours Tab */}
        <TabsContent value="tours">
          {myTours.length > 0 ? (
            <div className="space-y-3">
              {myTours.map(tour => {
                const statusColors = {
                  pending: 'bg-amber-100 text-amber-700',
                  accepted: 'bg-green-100 text-green-700',
                  rescheduled: 'bg-blue-100 text-blue-700',
                  cancelled: 'bg-red-100 text-red-700',
                };
                const statusLabels = language === 'es'
                  ? { pending: 'Pendiente', accepted: 'Confirmado', rescheduled: 'Reprogramado', cancelled: 'Cancelado' }
                  : { pending: 'Pending', accepted: 'Confirmed', rescheduled: 'Rescheduled', cancelled: 'Cancelled' };
                return (
                  <div key={tour.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-border hover:shadow-sm transition-shadow" data-testid={`tour-${tour.id}`}>
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Video className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/property/${tour.property_id}`} className="font-semibold text-sm truncate hover:text-[#D4AF37] transition-colors">{tour.property_title}</Link>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><User className="w-3 h-3" />{tour.agent_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs font-medium text-slate-600 flex items-center gap-1"><Calendar className="w-3 h-3 text-primary" />{tour.date}</span>
                        <span className="text-xs font-medium text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3 text-primary" />{tour.time}</span>
                      </div>
                      {tour.comments?.length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1 italic">"{tour.comments[tour.comments.length - 1].text}"</p>
                      )}
                    </div>
                    <Badge className={statusColors[tour.status] || 'bg-slate-100 text-slate-500'} data-testid={`tour-status-${tour.id}`}>
                      {statusLabels[tour.status] || tour.status}
                    </Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-border" data-testid="no-tours">
              <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-heading font-semibold text-lg text-slate-700">{tt('No Tours Yet', 'Sin Tours Aun')}</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">{tt('Book a virtual tour from any property page.', 'Reserva un tour virtual desde cualquier propiedad.')}</p>
              <Link to="/search">
                <Button className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="browse-properties-btn">
                  <Search className="w-4 h-4" />{tt('Browse Properties', 'Explorar Propiedades')}
                </Button>
              </Link>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
